/* Read-only availability checks. Shopify's cart API remains authoritative. */
(() => {
  'use strict';
  const format = (text, values) => text.replace(/\{(\w+)\}/g, (match, key) => String(values[key] ?? match));
  const label = product => product.name + (product.variantTitle && product.variantTitle !== 'Default Title' && !product.name.includes(product.variantTitle) ? ` (${product.variantTitle})` : '');
  function selectedEntries(entries) {
    const combined = new Map();
    for (const entry of entries) {
      if (!Number.isInteger(entry.quantity) || entry.quantity <= 0) continue;
      const key = String(entry.variantId), previous = combined.get(key);
      combined.set(key, {...entry, quantity: entry.quantity + (previous?.quantity || 0)});
    }
    return [...combined.values()];
  }
  function issuesFor(entries, availability, cart, editedGroup) {
    return selectedEntries(entries).flatMap(entry => {
      const stock = availability.get(String(entry.variantId));
      if (!stock || (stock.limit === null && stock.available)) return [];
      const limit = stock.available ? stock.limit : 0;
      if (!Number.isInteger(limit) || limit < 0) return [];
      const inCart = (cart?.items || []).filter(item => String(item.variant_id) === String(entry.variantId)
        && (!editedGroup || item.properties?._hebeso_set_id !== editedGroup)).reduce((sum, item) => sum + item.quantity, 0);
      const available = Math.max(0, limit - inCart);
      return entry.quantity > available ? [{...entry, inCart, available, limit, edited: !!editedGroup}] : [];
    });
  }
  async function loadAvailability(entries) {
    const results = await Promise.allSettled(selectedEntries(entries).map(async entry => {
      const url = new URL(entry.source, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid product origin');
      url.search = '';
      url.searchParams.set('variant', entry.variantId);
      url.searchParams.set('section_id', 'hebeso-product-availability');
      url.searchParams.set('_hebeso_stock', Date.now());
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url.href, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
        if (!response.ok) throw new Error('Availability unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(doc.querySelector('[data-hebeso-availability]')?.textContent || 'null');
        if (!data || String(data.variantId) !== String(entry.variantId) || typeof data.available !== 'boolean'
          || !(data.limit === null || Number.isInteger(data.limit) && data.limit >= 0)) throw new Error('Invalid availability');
        return [String(entry.variantId), data];
      } finally { clearTimeout(timeout); }
    }));
    // An unavailable check never invents stock or blocks an otherwise valid order.
    // The regular cart request still enforces availability on Shopify's server.
    return new Map(results.filter(result => result.status === 'fulfilled').map(result => result.value));
  }
  function create(words) {
    function messageFor(issues) {
      const lines = issues.map(issue => format(words.quantity, {name:label(issue), available:issue.available, selected:issue.quantity})
        + (issue.inCart ? ' ' + format(issue.edited ? words.other_cart : words.in_cart, {count:issue.inCart}) : ''));
      return words.heading + '\n' + lines.join('\n\n') + '\n' + words.action;
    }
    async function assertAvailable(entries, cart, editedGroup) {
      const issues = issuesFor(entries, await loadAvailability(entries), cart, editedGroup);
      if (issues.length) {
        const error = new Error(messageFor(issues));
        error.code = 'HEBESO_STOCK'; error.issues = issues;
        throw error;
      }
    }
    async function describeFailure(error, entries, cartBefore, editedGroup) {
      if (error.code === 'HEBESO_STOCK' || error.endpoint !== 'cart/add.js') return error.message;
      if (error.status === 422) {
        const issues = issuesFor(entries, await loadAvailability(entries), cartBefore, editedGroup);
        if (issues.length) return messageFor(issues);
      }
      const names = selectedEntries(entries).map(label).join(', ');
      // If Shopify does not identify one item, label the selection honestly.
      return format(words.selection_error, {names}) + '\n' + error.message;
    }
    return {assertAvailable, describeFailure, label, messageFor};
  }
  window.HebesoStockGuard = {create, issuesFor, selectedEntries};
})();
